import { useTranslation } from 'react-i18next';
import { CellStyle } from '../api/types';

const FONT_FAMILIES = ['Arial', 'Georgia', 'Times New Roman', 'Courier New', 'Verdana', 'Trebuchet MS'];

interface FormatToolbarProps {
  label: string;
  style: CellStyle;
  onChange: (patch: Partial<CellStyle>) => void;
  onClear: () => void;
  onClose: () => void;
}

function ToggleButton({ active, onClick, title, children }: { active: boolean; onClick: () => void; title: string; children: React.ReactNode }) {
  return (
    <button
      type="button"
      className="btn"
      onClick={onClick}
      title={title}
      style={active ? { background: 'var(--color-primary)', color: '#fff', borderColor: 'var(--color-primary)' } : undefined}
    >
      {children}
    </button>
  );
}

function ColorField({
  value,
  onChange,
  onClear,
  title,
}: {
  value: string | undefined;
  onChange: (v: string) => void;
  onClear: () => void;
  title: string;
}) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
      <input type="color" value={value ?? '#ffffff'} onChange={(e) => onChange(e.target.value)} title={title} style={{ width: 28, height: 28, padding: 0 }} />
      {value && (
        <button type="button" className="btn" style={{ padding: '1px 6px' }} onClick={onClear} title={title}>
          ×
        </button>
      )}
    </span>
  );
}

export function FormatToolbar({ label, style, onChange, onClear, onClose }: FormatToolbarProps) {
  const { t } = useTranslation();

  return (
    <div className="card format-toolbar" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
      <strong style={{ fontSize: 13 }}>{label}</strong>

      <select value={style.fontFamily ?? ''} onChange={(e) => onChange({ fontFamily: e.target.value || undefined })} title={t('format.fontFamily') as string}>
        <option value="">{t('format.defaultFont')}</option>
        {FONT_FAMILIES.map((f) => (
          <option key={f} value={f} style={{ fontFamily: f }}>
            {f}
          </option>
        ))}
      </select>

      <input
        type="number"
        min={8}
        max={72}
        placeholder={t('format.fontSize') as string}
        value={style.fontSize ?? ''}
        onChange={(e) => onChange({ fontSize: e.target.value ? parseInt(e.target.value, 10) : undefined })}
        style={{ width: 60 }}
        title={t('format.fontSize') as string}
      />

      <ToggleButton active={!!style.bold} onClick={() => onChange({ bold: !style.bold })} title={t('format.bold') as string}>
        <strong>G</strong>
      </ToggleButton>
      <ToggleButton active={!!style.italic} onClick={() => onChange({ italic: !style.italic })} title={t('format.italic') as string}>
        <em>I</em>
      </ToggleButton>
      <ToggleButton active={!!style.underline} onClick={() => onChange({ underline: !style.underline })} title={t('format.underline') as string}>
        <span style={{ textDecoration: 'underline' }}>S</span>
      </ToggleButton>
      <ToggleButton active={!!style.strikethrough} onClick={() => onChange({ strikethrough: !style.strikethrough })} title={t('format.strikethrough') as string}>
        <span style={{ textDecoration: 'line-through' }}>B</span>
      </ToggleButton>

      <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
        {t('format.textColor')}
        <ColorField value={style.color} onChange={(v) => onChange({ color: v })} onClear={() => onChange({ color: undefined })} title={t('format.textColor') as string} />
      </label>

      <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
        {t('format.background')}
        <ColorField
          value={style.backgroundColor}
          onChange={(v) => onChange({ backgroundColor: v })}
          onClear={() => onChange({ backgroundColor: undefined })}
          title={t('format.background') as string}
        />
      </label>

      <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
        {t('format.highlight')}
        <ColorField
          value={style.highlightColor}
          onChange={(v) => onChange({ highlightColor: v })}
          onClear={() => onChange({ highlightColor: undefined })}
          title={t('format.highlight') as string}
        />
      </label>

      <button type="button" className="btn" onClick={onClear}>
        {t('format.clear')}
      </button>
      <button type="button" className="btn" onClick={onClose}>
        {t('common.close')}
      </button>
    </div>
  );
}
